import { z } from "zod";
import { pagedResponse, paginationQuerySchema } from "../common/pagination.js";
export const captureInfo = z.object({
    id: z.string().uuid(),
    taskId: z.string().uuid().nullable(),
    sessionId: z.string().uuid(),
    source: z.string(),
    fileUrl: z.string().url().nullable(),
    fileUri: z.string(),
    sourceHash: z.string(),
    capturedAt: z.string().datetime(),
    frameIndex: z.number().int().nullable(),
    geoLat: z.number().nullable(),
    geoLng: z.number().nullable(),
    pose: z.record(z.unknown()),
    metadata: z.record(z.unknown()),
    createdAt: z.string().datetime(),
});
/**
 * Capture ordering for the listing. `created_asc` = oldest capture first,
 * `created_desc` = newest first, `modified_desc` = most recently updated first,
 * `by_task` = grouped by task in the same order as the "Task N" badges (the
 * task's first-target order_index, then its created_at), then capture time.
 * Omitted → each endpoint keeps its own default (session: oldest-first; task:
 * recently-modified-first).
 */
export const captureSort = z.enum([
    "created_asc",
    "created_desc",
    "modified_desc",
    "by_task",
]);
export const listCapturesQuery = paginationQuerySchema.extend({
    sort: captureSort.optional(),
});
export const capturesListResponse = pagedResponse(captureInfo);
export const uploadCaptureResponse = captureInfo;
// ── Capture analysis (annotated viewer) ──────────────────────────────────────
// Bounding boxes are normalised to top-left origin pixel coordinates in the
// ORIGINAL image space: { x, y, width, height }. The service accepts the
// vision payloads' `{x1,y1,x2,y2}` or `{x,y,width,height}` and normalises here.
export const detectionBox = z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
});
/** A vision-recommended product for a box that needs human review. */
/**
 * Vision's `auto_search` block, narrowed to the fields the UI renders. Kept
 * deliberately loose on absent values — vision omits fields it could not
 * determine, and a stricter shape would reject otherwise-usable proposals.
 */
export const newProductProposal = z.object({
    title: z.string().nullish(),
    brand: z.string().nullish(),
    size: z.string().nullish(),
    imageLink: z.string().nullish(),
    sourceLink: z.string().nullish(),
    sourceName: z.string().nullish(),
    confidence: z.number().nullish(),
    cropSha256: z.string().nullish(),
    /**
     * Pack-grouping keys. Vision groups duplicate facings of one unknown pack
     * under a `clusterId` and flags one representative; the UI uses these to find
     * the pack's single review task from any of its facings.
     */
    clusterId: z.number().nullish(),
    isClusterRepresentative: z.boolean().nullish(),
});
export const detectionSuggestion = z.object({
    productId: z.string().uuid(),
    productName: z.string().nullable(),
    productArticleCode: z.string().nullable(),
    productImageId: z.string().uuid().nullable(),
    similarity: z.number().nullable(),
});
/**
 * One `price_tag_detections.metadata->fieldStates` entry, verbatim: `value` is
 * populated for both `ok` AND `unverified` statuses (only the latter can reach
 * here uncorroborated), `status` is the raw field-level parse state.
 */
export const priceTagFieldState = z.object({
    value: z.string().nullable(),
    status: z.string(),
});
/**
 * Price/barcode reading for a `price_tag` / `promo_tag` detection, joined in
 * from its linked `price_tag_detections` row so the canvas tooltip can show
 * the detected price even when it's only `unverified` (the detection row
 * itself never carries it). `null` for every other detection type.
 */
export const analysisDetectionPriceTag = z.object({
    currency: z.string().nullable(),
    sellingPrice: priceTagFieldState.nullable(),
    regularPrice: priceTagFieldState.nullable(),
    barcode: priceTagFieldState.nullable(),
});
export const analysisDetection = z.object({
    id: z.string().uuid(),
    detectionType: z.string(),
    detectionIndex: z.number().int(),
    label: z.string().nullable(),
    confidence: z.number().nullable(),
    similarityScore: z.number().nullable(),
    recognitionSource: z.string(),
    facingCount: z.number().int(),
    bbox: detectionBox.nullable(),
    productId: z.string().uuid().nullable(),
    productName: z.string().nullable(),
    brand: z.string().nullable(),
    /** Detection's view/face classification from vision (e.g. "front"). */
    view: z.string().nullable(),
    // Human-review state. `needsReview` boxes weren't confidently matched but
    // carry vision `suggestions` to accept / reject / reassign.
    decision: z.string().nullable(),
    /**
     * Web-matched proposal for an ADD_TO_DB box: what vision thinks this pack
     * is. Present on every such detection, including duplicate facings that
     * produce no review candidate of their own, so any box can describe itself
     * without depending on a task existing.
     */
    newProductProposal: newProductProposal.nullable(),
    /**
     * Vision flagged the recognition as low-confidence (top1/top2 too close, or
     * a flag from the recogniser). Captured at parse time from
     * `recognition.items[].is_doubt`. Feeds the capture-level Doubt Rate %
     * tile (spec §6).
     */
    isDoubt: z.boolean(),
    /** OOS classification; legacy RGB findings default to no_product_detected. */
    reason: z.string().nullable().optional(),
    recessionMm: z.number().nullable().optional(),
    validDepthFraction: z.number().min(0).max(1).nullable().optional(),
    imageSha256: z.string().nullable().optional(),
    detectionIndices: z.array(z.number().int()).optional(),
    needsReview: z.boolean(),
    reviewedAt: z.string().datetime().nullable(),
    suggestions: z.array(detectionSuggestion),
    /** Null for every detectionType other than `price_tag` / `promo_tag`. */
    priceTag: analysisDetectionPriceTag.nullable(),
});
/** A candidate for human review, kept separate from operational OOS findings. */
export const captureReviewRegion = z.object({
    index: z.number().int(),
    reason: z.string(),
    bbox: detectionBox,
    shelfId: z.union([z.string(), z.number()]).nullable(),
    recessionMm: z.number().nullable(),
    validDepthFraction: z.number().min(0).max(1).nullable(),
    imageSha256: z.string().nullable(),
    detectionIndices: z.array(z.number().int()),
});
/** Body for accepting/rejecting a detection's recognition review. */
export const reviewDetectionRequest = z
    .object({
    action: z.enum(["accept", "reject"]),
    // For accept: the product to assign — a suggestion's productId or any
    // catalog product (reassign). Required on accept, ignored on reject.
    productId: z.string().uuid().optional(),
})
    .refine((v) => v.action !== "accept" || v.productId !== undefined, {
    message: "productId is required when action is 'accept'",
});
export const reviewDetectionResponse = z.object({ ok: z.literal(true) });
/**
 * Assign a catalogue product to an ADD_TO_DB ("New product") detection — the
 * product just created from its proposal, or an existing one it was matched to.
 * `applyToCluster` (default on) also relinks every other facing of the same
 * pack. The chosen SKU is marked on those boxes.
 */
export const linkDetectionProductRequest = z.object({
    productId: z.string().uuid(),
    applyToCluster: z.boolean().default(true),
});
export const linkDetectionProductResponse = z.object({
    detectionsLinked: z.number().int(),
});
// A fitted shelf "bifurcation" line in DETECTION-image pixel space, expressed
// as `y = lineSlope * x + lineIntercept`. `endpoints` caches the y at the left
// (x=0) and right (x=width-1) edges so a viewer can draw the segment without
// re-deriving it. All null for legacy runs that only recorded a y-band.
export const shelfLineEndpoints = z.object({
    x0Y: z.number(), // y where x = 0
    xWm1Y: z.number(), // y where x = detectionWidth - 1
});
/** One SKU's share of a shelf, from vision `report.shelf_level[i].skus[]`. */
export const shelfSkuShare = z.object({
    // The vision report keys SKUs by id; "unknown" is a real bucket (KPIs.md §4.4),
    // passed through verbatim. null means the field was absent from the payload.
    skuId: z.string().nullable(),
    facings: z.number(),
    // share fractions 0..1; by_length is the merchandising headline (KPIs.md §4.4).
    shareByLength: z.number(),
    shareByCount: z.number(),
    shareByArea: z.number(),
});
export const analysisShelfSection = z.object({
    id: z.string().uuid(),
    sectionType: z.string(),
    sectionIndex: z.number().int(),
    yBandLo: z.number().nullable(),
    yBandHi: z.number().nullable(),
    lineSlope: z.number().nullable(),
    lineIntercept: z.number().nullable(),
    endpoints: shelfLineEndpoints.nullable(),
    totalFacings: z.number().int().nullable(),
    /** Bounding box enclosing the shelf region (from vision `shelf_bboxes`). */
    bbox: detectionBox.nullable(),
    // ── per-shelf OOS metrics (vision oos.shelves[i]) ──
    productCount: z.number().int().nullable(),
    occupiedWidthRatio: z.number().nullable(),
    unoccupiedWidthRatio: z.number().nullable(),
    oosGapWidthRatio: z.number().nullable(),
    largestGapRatio: z.number().nullable(),
    emptyRegionCount: z.number().int().nullable(),
    oosSeverity: z.string().nullable(),
    // ── per-shelf SOS (vision report.shelf_level[i].skus[]) ──
    skuBreakdown: z.array(shelfSkuShare),
});
export const captureAnalysisSummary = z.object({
    skuCount: z.number().int(),
    facings: z.number().int(),
    priceTags: z.number().int(),
    oosCount: z.number().int(),
    reviewRegionCount: z.number().int().optional(),
    // null when there is nothing to measure (no products and no OOS regions).
    oosRate: z.number().nullable(),
    recognized: z.number().int(),
    unrecognized: z.number().int(),
    // Boxes awaiting human accept/reject (UNCERTAIN / NOT_IN_DB with suggestions).
    needsReview: z.number().int(),
});
export const captureAnalysisResponse = z.object({
    // Identity/time of the completed evidence being displayed, even during a rerun.
    visionRunId: z.string().uuid().nullable().optional(),
    analyzedAt: z.string().nullable().optional(),
    capture: z.object({
        id: z.string().uuid(),
        capturedAt: z.string().nullable().optional(),
        fileUrl: z.string().url().nullable(),
        widthPx: z.number().int().nullable(),
        heightPx: z.number().int().nullable(),
        detectionWidth: z.number().int().nullable(),
        detectionHeight: z.number().int().nullable(),
        fixtureId: z.string().uuid().nullable(),
        storeId: z.string().uuid().optional(),
        taskId: z.string().uuid().nullable(),
        sessionId: z.string().uuid(),
    }),
    hasVisionRun: z.boolean(),
    // Status of the most recent vision run for this capture (any state), so the
    // UI can show "in progress" / "failed" instead of an empty page while the
    // worker is still processing. null = no run has ever been created.
    runStatus: z.enum(["queued", "processing", "completed", "failed", "cancelled"]).nullable(),
    summary: captureAnalysisSummary,
    /** Vision's aggregate OOS totals/widths/severity, preserved as supplied. */
    oosSummary: z.record(z.unknown()).nullable().optional(),
    shelfSections: z.array(analysisShelfSection),
    detections: z.array(analysisDetection),
    reviewRegions: z.array(captureReviewRegion).optional(),
    // Rack region of interest from vision (`shelves.rack_roi`), in the same
    // detection pixel space as the boxes; the UI crops to it. null = not sent.
    rackRoi: detectionBox.nullable(),
});
export const assignCaptureFixtureRequest = z
    .object({
    fixtureId: z.string().uuid(),
    expectedFixtureId: z.string().uuid().nullable(),
})
    .strict();
export const assignCaptureFixtureResponse = z.object({
    captureId: z.string().uuid(),
    fixtureId: z.string().uuid(),
    changed: z.boolean(),
});
// ── Robot capture attachments ────────────────────────────────────────────────
// Extra frames a depth-camera robot produces alongside the as-is RGB capture.
// Entirely optional: a capture with no attachments carries no `attachments` key
// in metadata and behaves exactly as it did before this existed.
/**
 * Kinds we currently know about. This is a DOCUMENTATION + UI hint list, not a
 * validation gate — see `attachmentKind` below.
 */
export const KNOWN_ATTACHMENT_KINDS = [
    "depth",
    "ir",
    "colorized_depth",
    "confidence",
    "point_cloud",
    "other",
];
/**
 * Free-form on purpose.
 *
 * A closed enum would couple every new robot capability to a backend deploy:
 * the robot starts emitting `depth_raw`, the API 422s, and that scan's depth
 * data is lost for good. The robot is the thing that knows what its sensors
 * produce, so it names them.
 *
 * Constrained only to a safe slug — normalized to lowercase, no spaces or
 * path separators — so a kind can never break JSON, corrupt a log line, or
 * become awkward if this is ever promoted to a real column.
 */
export const attachmentKind = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .transform((s) => s.toLowerCase())
    .refine((s) => /^[a-z0-9][a-z0-9_.-]*$/.test(s), {
    message: "kind must start with a letter or digit and contain only letters, digits, '_', '.' or '-'",
});
/** Max extra files per capture — mirrors the multipart `files` limit. */
export const MAX_ATTACHMENTS = 9;
/**
 * Wire shape (camelCase). Stored in `captures.metadata.attachments[]` in
 * snake_case, matching the sibling system fields already in that column
 * (`sha256`, `content_type`, `declared_content_type`, ...).
 */
export const captureAttachment = z.object({
    id: z.string().uuid(),
    kind: attachmentKind,
    objectKey: z.string(),
    sha256: z.string(),
    contentType: z.string(),
    declaredContentType: z.string(),
    fileName: z.string(),
    bytes: z.number().int().nonnegative(),
    uploadedAt: z.string().datetime(),
    meta: z.record(z.unknown()),
});
/**
 * One entry of the `attachments` multipart field on the ride-along upload.
 * Matched to its file part BY `fileName`, never by position — a reordered
 * multipart must not silently relabel a depth map as a point cloud.
 */
export const attachmentDescriptor = z.object({
    fileName: z.string().min(1),
    kind: attachmentKind,
    meta: z.record(z.unknown()).default({}),
});
export const attachmentDescriptorList = z.array(attachmentDescriptor).max(MAX_ATTACHMENTS);
export const uploadAttachmentResponse = z.object({
    captureId: z.string().uuid(),
    attachment: captureAttachment,
    /** True when an entry with the same (sha256, kind) already existed. */
    deduplicated: z.boolean(),
    /** True when a vision run for this capture was already queued/running. */
    analysisAlreadyStarted: z.boolean(),
});
export const finalizeCaptureResponse = z.object({
    captureId: z.string().uuid(),
    enqueued: z.boolean(),
});
//# sourceMappingURL=captures.schemas.js.map